/* eslint-disable */
import { addDays, format, parseISO as parse, intervalToDuration } from 'date-fns';

export const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

export const parseISO = parse;

export const isDate = (value) => value instanceof Date && !isNaN(value);

export const formatDate = (date, formatOutput = ' h:mm aa') => {
  if (!date || !formatOutput) {
    return '-';
  }

  if (typeof date === 'string') {
    date = parseISO(date);
  }

  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  return format(date, formatOutput);
};

export const getDateObject = (date) => {
  if (typeof date === 'string') {
    if (!date.endsWith('Z')) {
      date = date + ' UTC';
    }

    return new Date(date);
  }

  return date;
};

const formatDistanceLocale = {
  lessThanXSeconds: '{count}s',
  xSeconds: '{count}s',
  halfAMinute: '30s',
  lessThanXMinutes: '{count}m',
  xMinutes: '{count}m',
  aboutXHours: '{count}h',
  xHours: '{count}h',
  xDays: '{count}d',
  aboutXWeeks: '{count}w',
  xWeeks: '{count}w',
  aboutXMonths: '{count}mth',
  xMonths: '{count}mth',
  aboutXYears: '{count}y',
  xYears: '{count}y',
  overXYears: '{count}y',
  almostXYears: '{count}y'
};

export const formatDistance = (token, count, options = {}) => {
  const result = formatDistanceLocale[token].replace('{count}', count);

  if (options.addSuffix) {
    if (options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
};

export const daysBetweenDates = (first, second) => {
  const diff = second.getTime() - first.getTime();
  return diff / (1000 * 60 * 60 * 24);
};

export const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const end = addDays(getDateObject(endDate), 1);
  let start = addDays(getDateObject(startDate), 1);

  while (start <= end) {
    dates.push(format(start, 'yyyy-MM-dd'));
    start = addDays(start, 1);
  }

  return dates;
};

export const getDateFromToday = (days) => {
  return Date.now() - days * MILLISECONDS_IN_DAY;
};

export const normalizeTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

export const durationFromSeconds = (value) => {
  const duration = intervalToDuration({ start: 0, end: Number(value) * 1000 });

  let formatted = '';

  if (duration.hours) {
    formatted += duration.hours + 'hr ';
  }

  if (duration.minutes) {
    formatted += duration.minutes + 'm ';
  }

  if (duration.seconds) {
    formatted += duration.seconds + 's';
  }

  if (!formatted.length) {
    formatted += value + 's';
  }

  return formatted;
};

export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date.toString() !== 'Invalid Date' && !isNaN(date);
};

export const DATE_TODAY_TOKEN = 'today';

/**
 * Decodes a date URL param. The `today` keyword resolves to a start-of-day
 * numeric timestamp; every other value (literal date strings, '', undefined)
 * is returned unchanged. Callers wrap the result in `new Date(...)`, which
 * accepts both a numeric epoch and a date string.
 */
export const resolveDateToken = (value) => {
  if (value === DATE_TODAY_TOKEN) {
    return new Date().setHours(0, 0, 0, 0);
  }

  return value;
};

/**
 * True when a date-range pair is in a *complete* state (both ends present, or
 * both cleared) AND differs from a previously captured pair. Used to gate a
 * refetch on a date range so a half-selected range (start chosen, end not yet
 * picked) does not trigger an intermediate, unfiltered request. Mirrors the
 * "both present or both cleared" guard the change handler used previously.
 */
export const dateRangePairChanged = (startDate, endDate, prevStartDate, prevEndDate) => {
  const pairIsComplete = (Boolean(startDate) && Boolean(endDate)) || (!startDate && !endDate);
  const changed = (startDate || '') !== (prevStartDate || '') || (endDate || '') !== (prevEndDate || '');

  return pairIsComplete && changed;
};
